import Vue from 'vue'
import VueRouter from 'vue-router'
import Cookies from 'js-cookie'
import { getSystemConfig } from '@/utils/config'

Vue.use(VueRouter)

import { userRoles, routeConfigs, specialRoutes } from './routes-config'

// 重定向 URL 常量
const ACCOUNT_URL_STABLE = 'https://www.yzdli.com'
const ACCOUNT_URL_BETA = 'http://beta.yunhzh.com'

// roleId 到 role 名称的映射
const roleIdToName = {
  1: 'visitor',
  2: 'organizer',
  3: 'exhibitor',
  4: 'contractors',
  5: 'designer',
}

// Role name 到 roleId 的映射
const roleNameToId = {
  visitor: 1,
  organizer: 2,
  exhibitor: 3,
  contractors: 4,
  designer: 5,
}

// Route generation factory function
// 使用路由配置中的 chunkName 进行代码分割
// 相同 chunkName 的路由会被打包到同一个 JS 文件中
const chunkImportMap = {}

function getChunkImport(component, chunkName) {
  // 为每个 chunkName 创建对应的动态导入函数
  // 这样 webpack 可以正确地进行代码分割
  const chunk = chunkName || 'misc'

  // 根据 chunkName 返回对应的动态导入
  switch (chunk) {
    case 'bidding':
      return () => import(/* webpackChunkName: "bidding" */ `@/pages/memberCenter/${component}`)
    case 'order':
      return () => import(/* webpackChunkName: "order" */ `@/pages/memberCenter/${component}`)
    case 'account':
      return () => import(/* webpackChunkName: "account" */ `@/pages/memberCenter/${component}`)
    case 'live':
      return () => import(/* webpackChunkName: "live" */ `@/pages/memberCenter/${component}`)
    case 'exhibit':
      return () => import(/* webpackChunkName: "exhibit" */ `@/pages/memberCenter/${component}`)
    case 'information':
      return () => import(/* webpackChunkName: "information" */ `@/pages/memberCenter/${component}`)
    case 'invoice':
      return () => import(/* webpackChunkName: "invoice" */ `@/pages/memberCenter/${component}`)
    case 'showroom':
      return () => import(/* webpackChunkName: "showroom" */ `@/pages/memberCenter/${component}`)
    case 'procurement':
      return () => import(/* webpackChunkName: "procurement" */ `@/pages/memberCenter/${component}`)
    case 'merchants':
      return () => import(/* webpackChunkName: "merchants" */ `@/pages/memberCenter/${component}`)
    case 'card':
      return () => import(/* webpackChunkName: "card" */ `@/pages/memberCenter/${component}`)
    case 'card-editor':
      return () => import(/* webpackChunkName: "card-editor" */ `@/pages/memberCenter/${component}`)
    case 'product':
      return () => import(/* webpackChunkName: "product" */ `@/pages/memberCenter/${component}`)
    case 'data':
      return () => import(/* webpackChunkName: "data" */ `@/pages/memberCenter/${component}`)
    case 'basic':
      return () => import(/* webpackChunkName: "basic" */ `@/pages/memberCenter/${component}`)
    case 'meeting':
      return () => import(/* webpackChunkName: "meeting" */ `@/pages/memberCenter/${component}`)
    case 'notifications':
      return () => import(/* webpackChunkName: "notifications" */ `@/pages/memberCenter/${component}`)
    case 'ram':
      return () => import(/* webpackChunkName: "ram" */ `@/pages/memberCenter/${component}`)
    case 'cces':
      return () => import(/* webpackChunkName: "cces" */ `@/pages/memberCenter/${component}`)
    case 'cces-editor':
      return () => import(/* webpackChunkName: "cces-editor" */ `@/pages/memberCenter/${component}`)
    case 'cces-count':
      return () => import(/* webpackChunkName: "cces-count" */ `@/pages/memberCenter/${component}`)
    case 'cces-recruit':
      return () => import(/* webpackChunkName: "cces-recruit" */ `@/pages/memberCenter/${component}`)
    case 'cces-offers':
      return () => import(/* webpackChunkName: "cces-offers" */ `@/pages/memberCenter/${component}`)
    case 'exhibition':
      return () => import(/* webpackChunkName: "exhibition" */ `@/pages/memberCenter/${component}`)
    case 'meeting-org':
      return () => import(/* webpackChunkName: "meeting-org" */ `@/pages/memberCenter/${component}`)
    case 'marketing':
      return () => import(/* webpackChunkName: "marketing" */ `@/pages/memberCenter/${component}`)
    case 'marketing-aicall':
      return () => import(/* webpackChunkName: "marketing-aicall" */ `@/pages/memberCenter/${component}`)
    case 'marketing-invite':
      return () => import(/* webpackChunkName: "marketing-invite" */ `@/pages/memberCenter/${component}`)
    case 'coupons':
      return () => import(/* webpackChunkName: "coupons" */ `@/pages/memberCenter/${component}`)
    case 'invite':
      return () => import(/* webpackChunkName: "invite" */ `@/pages/memberCenter/${component}`)
    default:
      return () => import(/* webpackChunkName: "misc" */ `@/pages/memberCenter/${component}`)
  }
}

// 需要重定向到首页的旧前缀
const legacyPrefixes = ['visitor', 'organizer', 'exhibitor', 'contractors', 'designer', 'account']

// 生成统一路由（直接使用根路径，无前缀）
function generateRoutes(config) {
  const { path: pathPattern, component, roles: allowedRoles, chunkName } = config
  return {
    path: `/${pathPattern}`,
    name: `page-${pathPattern.replace(/\//g, '-').replace(/:/g, '')}`,
    component: getChunkImport(component, chunkName),
    meta: {
      allowedRoles: allowedRoles || null, // null 表示所有角色可访问
    },
  }
}

// 生成旧路由的兼容重定向（所有旧前缀都重定向到根路径对应路由）
function generateLegacyRedirects(config) {
  const { path: pathPattern } = config
  return legacyPrefixes.map((prefix) => ({
    path: `/${prefix}/${pathPattern}`,
    redirect: `/${pathPattern}`,
  }))
}

// 生成新的统一路由
const generatedRoutes = routeConfigs.map((config) => generateRoutes(config))

// 生成旧路由兼容重定向
const legacyRedirects = routeConfigs.map((config) => generateLegacyRedirects(config)).flat()

// 旧前缀首页重定向到根路径
const legacyPrefixIndexRedirects = legacyPrefixes.map((prefix) => ({
  path: `/${prefix}`,
  redirect: '/',
}))

// 首页路由（根路径）
const indexRoute = {
  path: '/',
  name: 'home-index',
  component: () => import(/* webpackChunkName: "account-index" */ '@/pages/index.vue'),
}

const routes = [
  // 首页（根路径）
  indexRoute,
  // 新的统一路由
  ...generatedRoutes,
  // 旧路由兼容重定向
  ...legacyRedirects,
  ...legacyPrefixIndexRedirects,
  // 特殊路由
  ...specialRoutes,
]

// console.log('完整路由:', routes);

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
})

// 获取原型对象上的 push 函数
const originalPush = VueRouter.prototype.push
// 修改原型对象中的 push 方法
VueRouter.prototype.push = function push(location) {
  // 调用旧的 push 方法，并捕获异常
  return originalPush.call(this, location).catch((err) => err)
}

// 获取原型对象上的 replace 函数
const originalReplace = VueRouter.prototype.replace
// 修改原型对象中的 replace 方法
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch((err) => err)
}

// 路由守卫：权限检查
router.beforeEach((to, from, next) => {
  // 1. 检查路由是否有角色限制
  if (to.meta?.allowedRoles && to.meta.allowedRoles.length > 0) {
    // 2. 获取用户的实际角色
    const cookieRoleId = Cookies.get('roleId')
    const roleId = cookieRoleId ? parseInt(cookieRoleId, 10) : null
    const userRole = roleIdToName[roleId]

    // 3. 检查用户是否有权限访问此页面
    if (!userRole || !to.meta.allowedRoles.includes(userRole)) {
      console.log(`[Router] Permission denied. User role: ${userRole}, Allowed roles: ${to.meta.allowedRoles}`)
      // 无权限，跳转到首页
      return next({ path: '/', replace: true })
    }
  }

  // 4. 检查路由是否匹配
  if (to.matched.length === 0) {
    // 未匹配的路由，重定向到首页
    const { envBoolean } = getSystemConfig()
    const baseUrl = envBoolean.STABLE ? ACCOUNT_URL_STABLE : ACCOUNT_URL_BETA

    // 外部重定向到主站（保留完整路径以便主站处理）
    if (process.client) {
      window.location.href = `${baseUrl}${to.fullPath}`
      return
    }
  }

  next()
})

export default router
